/**
 * Error Interceptor
 * Handles HTTP error responses globally.
 * Manages 401 (session expired), 403 (forbidden), and other error codes.
 * Uses TranslateService to display localized error messages.
 */

import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, retry, throwError, timer } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { AppSnackbarService } from '../notifications/snackbar.service';
import { NavigationGenerationService } from '../services/navigation-generation.service';

/** HTTP status codes that should not display a generic error message */
const SILENT_STATUS_CODES = new Set<number>([304]);

/** Status codes that usually mean "the call was briefly interrupted" (e.g. a
 *  cold backend instance on the first request of the day), not a real,
 *  persistent failure — worth a couple of quick, silent retries before ever
 *  surfacing an error. */
const TRANSIENT_STATUS_CODES = new Set<number>([0, 408, 502, 503, 504]);
const TRANSIENT_RETRY_COUNT = 2;
const TRANSIENT_RETRY_DELAY_MS = 600;

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const snackbarService = inject(AppSnackbarService);
  const translateService = inject(TranslateService);
  const navigationGeneration = inject(NavigationGenerationService);
  const skipSnackbar = req.headers?.get?.('X-Skip-Error-Snackbar') === '1';
  const skipAuthLogout = req.headers?.get?.('X-Skip-Auth-Logout') === '1';
  const requestGeneration = navigationGeneration.current();

  return next(req).pipe(
    retry({
      count: TRANSIENT_RETRY_COUNT,
      delay: (error: HttpErrorResponse, retryAttempt) => {
        // Only retry safe (GET) requests failing with a transient status —
        // anything else should fall straight through to the usual handling.
        if (req.method !== 'GET' || !TRANSIENT_STATUS_CODES.has(error.status)) {
          throw error;
        }
        return timer(TRANSIENT_RETRY_DELAY_MS * retryAttempt);
      },
    }),
    catchError((error: HttpErrorResponse) => {
      if (SILENT_STATUS_CODES.has(error.status)) {
        return throwError(() => error);
      }

      // The user has since navigated away from whatever triggered this call —
      // showing a generic error toast for a page they no longer see just
      // reads as "something's broken" when it's really a stale, abandoned request.
      const isStale = navigationGeneration.current() !== requestGeneration;

      if (!skipSnackbar && !isStale) {
        showErrorSnackbar(error, snackbarService, translateService, req.method);
      }

      if (error.status === 401 && !skipAuthLogout) {
        void handleUnauthorized(req, authService, router);
      }

      if (error.status === 403) {
        router.navigate(['/access-denied']);
      }

      return throwError(() => error);
    })
  );
};

/**
 * Display an error snackbar, preferring the backend message over the generic i18n fallback.
 */
function showErrorSnackbar(
  error: HttpErrorResponse,
  snackbarService: AppSnackbarService,
  translateService: TranslateService,
  method: string,
): void {
  const backendMsg: string | null =
    (typeof error.error?.errorMessage === 'string' ? error.error.errorMessage : null) ??
    (typeof error.error?.message === 'string' ? error.error.message : null) ??
    (typeof error.error?.detail === 'string' ? error.error.detail : null);

  if (backendMsg) {
    snackbarService.error(backendMsg);
    return;
  }

  // GET requests are passive background reads (dashboards, filters, list screens).
  // Without a specific backend message, a generic "server error" toast for one of
  // several parallel reads reads as "the system is always broken" for something the
  // user didn't even trigger. User-initiated writes (POST/PUT/DELETE/PATCH) still
  // need to surface *something* even without a specific backend message.
  if (method === 'GET') {
    return;
  }

  const i18nKey = resolveErrorKey(error);
  translateService.get(i18nKey).subscribe((translatedMessage: string) => {
    const message = translatedMessage === i18nKey ? 'An unexpected error occurred.' : translatedMessage;
    snackbarService.error(message);
  });
}

/**
 * Handle 401 Unauthorized: trigger logout only for authenticated or main-API requests.
 * Skips logout when the user recently authenticated (post-login grace period) to avoid
 * false-positive logouts caused by server-side token propagation delay.
 */
async function handleUnauthorized(
  req: import('@angular/common/http').HttpRequest<unknown>,
  authService: AuthService,
  router: Router,
): Promise<void> {
  // Skip logout when the user just logged in — some server nodes may take a few
  // seconds to propagate/validate the newly issued token (e.g. clock skew,
  // session replication delay). Logging out in this window causes the
  // user to be kicked back to the login page immediately after a successful login.
  if (authService.isRecentlyAuthenticated()) {
    console.warn('401 received within post-login grace period; skipping logout.', req.url);
    return;
  }
  // Always attempt logout unless the user was recently authenticated. Navigation to login
  // is performed here to ensure consistent behavior in tests and environments where
  // AuthService.logout() may not perform navigation itself.
  authService.logout();
  try {
    await router.navigate(['/login']);
  } catch {
    // ignore navigation errors in unit tests or environments without router
  }
}

/**
 * Resolve the i18n key for a given HTTP error status
 */
function resolveErrorKey(error: HttpErrorResponse): string {
  const statusKeyMap: Record<number, string> = {
    0: 'errors.http.connection',
    400: 'errors.http.bad_request',
    401: 'errors.http.unauthorized',
    403: 'errors.http.forbidden',
    404: 'errors.http.not_found',
    408: 'errors.http.timeout',
    422: 'errors.http.unprocessable',
    429: 'errors.http.too_many_requests',
    500: 'errors.http.server_error',
    502: 'errors.http.bad_gateway',
    503: 'errors.http.service_unavailable'
  };

  return statusKeyMap[error.status] || 'errors.http.unknown';
}
